/**
 * Council Trail v0 — CouncilContext.
 *
 * Provider hydrates from GET /api/sessions/{id}/council on mount + fetches
 * each roster persona via Promise.all over /api/personas/{id} to populate
 * personasById (plan rev 2 substantive #8 C5 — used by Task 5.5
 * useMessagesPeerMetadata to resolve peer-author metadata without a
 * round-trip per message).
 *
 * Plan rev 2 noted a batch /api/personas?ids=... endpoint; that doesn't
 * exist in v0. Individual GETs in parallel is correct for rosters under
 * ~10 peers; v0.1+ may add a batch endpoint if profiling justifies it.
 */
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type {
  BudgetOvershootWire,
  CouncilCost,
  CouncilState,
  InvitePeersInput,
  InvitePeersOutput,
  NudgePeerInput,
  NudgePeerOutput,
  PeerEventWire,
} from '../types/council';
import type { Persona } from '../types/persona';
import { councilApiClient } from '../services/councilApiClient';
import { councilWsClient } from '../services/councilWsClient';
import { reconcileOnRefresh, upsertOvershoot, normalizeOvershoot } from '../services/councilCost';

export interface CouncilContextValue {
  state: CouncilState | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  inviteCrew: (payload: InvitePeersInput) => Promise<InvitePeersOutput>;
  nudge: (payload: NudgePeerInput) => Promise<NudgePeerOutput>;
  dismiss: (personaId: string) => Promise<void>;
  /**
   * v0.1.x — write-through cache: optimistic local update + PATCH to
   * /api/sessions/{id}/council/metadata. On server confirmation the
   * full CouncilState payload replaces our local state. On error the
   * optimistic write rolls back to the prior value.
   */
  setHillToClimb: (next: string | null) => Promise<void>;
}

export const CouncilContext = createContext<CouncilContextValue | null>(null);

async function _fetchPersonaSafe(id: string): Promise<Persona | null> {
  try {
    const r = await fetch(`/api/personas/${id}`, { credentials: 'same-origin' });
    if (!r.ok) return null;
    return (await r.json()) as Persona;
  } catch {
    return null;
  }
}

export function CouncilProvider({
  sessionId,
  children,
}: {
  sessionId: string;
  children: React.ReactNode;
}) {
  const [state, setState] = useState<CouncilState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Capture the fetch-start BEFORE the await — reconcileOnRefresh keeps live-only
    // breaches stamped after this instant (genuine in-flight race), evicts older ones.
    const fetchStart = new Date().toISOString();
    try {
      const base = await councilApiClient.getCouncilState(sessionId);
      const roster = ((base as any).roster ?? []) as Array<{ persona_id: string }>;
      const ids = roster.map(r => r.persona_id);
      const personas = await Promise.all(ids.map(_fetchPersonaSafe));
      const personasById: Record<string, Persona> = {};
      for (const p of personas) {
        if (p && p.id) personasById[p.id] = p;
      }
      // INVARIANT: keep this a FUNCTIONAL setState(prev => …). A plain setState({...})
      // would capture stale `state`, forcing `state`/`cost` into the dep array, recreating
      // `refresh` on every cost change → the WS-subscribe effect ([sessionId, refresh])
      // churns and reconnects the socket each breach. Do not "simplify" it.
      setState(prev => {
        const base2 = base as CouncilState;
        const serverCost = (base2.cost && (base2.cost as any).cost_cap_usd_per_day)
          ? base2.cost as CouncilCost : undefined;
        const cost = serverCost
          ? { ...serverCost, overshoot_events: reconcileOnRefresh(serverCost.overshoot_events, prev?.cost?.overshoot_events, fetchStart) }
          : prev?.cost;  // server sent {} → keep prior
        return { ...base2, personasById, cost };
      });
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh();
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  // v0.1 Item 8 fix — refresh on peer_invited / peer_dismissed so the
  // roster (and therefore TrailheadHeader's councilMode gate) updates
  // live when peers come/go via ANY path: explicit inviteCrew, manual
  // /council/invite POST, or the Hill Climbers lab's auto-seed.
  // Without this, council-mode chrome stayed hidden after a backend-
  // side roster change and the operator saw no visible effect.
  useEffect(() => {
    const unsub = councilWsClient.subscribe(sessionId, (event: PeerEventWire) => {
      // ws_connected = synthetic event from the client on every (re)connect.
      // Refresh catches us up on roster changes that fired while we were
      // disconnected (HMR, network blip, backend reload).
      // proposal_resolved fires after every approve/dismiss; for approves
      // that mutate council metadata (propose_chip → hill_to_climb) this is
      // the only way the chip surface picks up the new value. (For
      // propose_invite, PEER_INVITED also fires, but the refresh is
      // idempotent so the duplicate is harmless.)
      if (
        event.event_type === 'peer_invited' ||
        event.event_type === 'peer_dismissed' ||
        (event.event_type as unknown as string) === 'proposal_resolved' ||
        (event.event_type as unknown as string) === 'governor_policy_changed' ||
        (event.event_type as unknown as string) === 'ws_connected'
      ) {
        void refresh();
      } else if ((event.event_type as unknown as string) === 'budget_overshoot') {
        // Live breach: upsert into the local cost list, do NOT refetch. Stamp detected_at
        // with the FE clock (NO event.timestamp — the backend wire ts is a naive Pydantic
        // datetime with no Z/offset and would mis-order against sinceIso in reconcile).
        const entry = normalizeOvershoot(event.target as unknown as BudgetOvershootWire);
        setState(prev => (prev?.cost
          ? { ...prev, cost: { ...prev.cost, overshoot_events: upsertOvershoot(prev.cost.overshoot_events, entry) } }
          : prev));   // no cost block yet → next refresh hydrates
      }
    });
    return unsub;
  }, [sessionId, refresh]);

  const inviteCrew = useCallback(async (payload: InvitePeersInput) => {
    const out = await councilApiClient.inviteCrew(sessionId, payload);
    await refresh();
    return out;
  }, [sessionId, refresh]);

  const nudge = useCallback(async (payload: NudgePeerInput) => {
    return councilApiClient.nudge(sessionId, payload);
  }, [sessionId]);

  const dismiss = useCallback(async (personaId: string) => {
    await councilApiClient.dismiss(sessionId, { persona_id: personaId });
    await refresh();
  }, [sessionId, refresh]);

  const setHillToClimb = useCallback(async (next: string | null) => {
    // Snapshot for rollback on error.
    let prior: string | null | undefined;
    setState(prev => {
      if (!prev) return prev;
      prior = prev.metadata?.hill_to_climb ?? null;
      return {
        ...prev,
        metadata: {
          ...prev.metadata,
          hill_to_climb: next,
        },
      };
    });
    try {
      const fresh = await councilApiClient.patchMetadata(sessionId, {
        hill_to_climb: next,
      });
      // Optionally fetch personas for any new roster rows; v0.1.x keeps
      // it simple — patch route returns the same shape as getCouncilState
      // so we replace the assembled fields directly while preserving the
      // already-hydrated personasById cache.
      setState(prev => {
        if (!prev) return fresh as CouncilState;
        const freshCost = (fresh as any).cost;
        const cost = (freshCost && freshCost.cost_cap_usd_per_day)
          ? { ...freshCost, overshoot_events: reconcileOnRefresh(freshCost.overshoot_events, prev.cost?.overshoot_events, new Date().toISOString()) }
          : prev.cost;  // patch route didn't carry cost → keep live entries
        return { ...prev, ...fresh, cost };
      });
    } catch (e) {
      // Roll back the optimistic write so the UI matches server truth.
      setState(prev => prev
        ? {
            ...prev,
            metadata: {
              ...prev.metadata,
              hill_to_climb: prior ?? null,
            },
          }
        : prev,
      );
      throw e;
    }
  }, [sessionId]);

  // Memoize the value — peer/merge nodes become useContext consumers (G4b Task 6); an
  // unmemoized fresh literal would re-render every visible node on any provider render.
  // All callbacks are useCallback-stable, so this is effective.
  const value = useMemo(() => ({
    state, loading, error, refresh,
    inviteCrew, nudge, dismiss, setHillToClimb,
  }), [state, loading, error, refresh, inviteCrew, nudge, dismiss, setHillToClimb]);

  return (
    <CouncilContext.Provider value={value}>
      {children}
    </CouncilContext.Provider>
  );
}
